import apiClient from './api'

const STORAGE_LOCALE_KEY = 'app_locale'
const STORAGE_TRANSLATIONS_KEY = 'app_translations'
const STORAGE_AUTO_TRANSLATION_JOB_KEY = 'i18n_auto_translation_active_job'

const SYNC_STAT_KEYS = {
  totalKeys: ['totalKeys', 'total_keys', 'extractedKeys', 'extracted_keys'],
  codeKeys: ['codeKeys', 'code_keys', 'extractedKeys', 'extracted_keys'],
  dbKeysBefore: ['dbKeysBefore', 'db_keys_before'],
  dbKeysAfter: ['dbKeysAfter', 'db_keys_after', 'canonical_key_count'],
  created: ['created', 'added', 'addedCount', 'added_count'],
  updated: ['updated', 'updatedCount', 'updated_count'],
  unchanged: ['unchanged', 'unchangedCount', 'unchanged_count'],
  deleted: ['deleted', 'deletedCount', 'deleted_count', 'removed', 'removedCount'],
  missing: ['missing', 'missingCount', 'missing_count'],
  locales: ['locales', 'localeCount', 'locale_count', 'language_count', 'enabled_language_count'],
  files: ['files', 'fileCount', 'file_count', 'scannedFiles', 'scanned_files', 'translation_file_count'],
  durationMs: ['durationMs', 'duration_ms', 'elapsedMs', 'elapsed_ms'],
}

function pickFirstDefined(source, keys = []) {
  if (!source || typeof source !== 'object') return undefined
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] != null) {
      return source[key]
    }
  }
  return undefined
}

function toOptionalNumber(value) {
  if (value == null || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function countArray(source, key) {
  if (!source || typeof source !== 'object') return undefined
  const value = source[key]
  return Array.isArray(value) ? value.length : undefined
}

function normalizeSyncStats(raw) {
  const report = raw?.report && typeof raw.report === 'object' ? raw.report : {}
  const direct = raw && typeof raw === 'object' ? raw : {}

  const stats = {
    totalKeys: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.totalKeys) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.totalKeys)),
    codeKeys: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.codeKeys) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.codeKeys)),
    dbKeysBefore: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.dbKeysBefore) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.dbKeysBefore)),
    dbKeysAfter: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.dbKeysAfter) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.dbKeysAfter)),
    created: toOptionalNumber(
      pickFirstDefined(report, SYNC_STAT_KEYS.created) ??
      pickFirstDefined(direct, SYNC_STAT_KEYS.created) ??
      countArray(report, 'addedKeys') ??
      countArray(direct, 'addedKeys'),
    ),
    updated: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.updated) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.updated)),
    unchanged: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.unchanged) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.unchanged)),
    deleted: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.deleted) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.deleted)),
    missing: toOptionalNumber(
      pickFirstDefined(report, SYNC_STAT_KEYS.missing) ??
      pickFirstDefined(direct, SYNC_STAT_KEYS.missing) ??
      countArray(report, 'missingKeys') ??
      countArray(direct, 'missingKeys'),
    ),
    locales: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.locales) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.locales)),
    files: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.files) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.files)),
    durationMs: toOptionalNumber(pickFirstDefined(report, SYNC_STAT_KEYS.durationMs) ?? pickFirstDefined(direct, SYNC_STAT_KEYS.durationMs)),
  }

  const compactStats = Object.fromEntries(
    Object.entries(stats).filter(([, value]) => value !== undefined),
  )

  return Object.keys(compactStats).length > 0 ? compactStats : undefined
}

function normalizeSyncErrors(raw) {
  const reportErrors = Array.isArray(raw?.report?.errors) ? raw.report.errors : []
  const errors = Array.isArray(raw?.errors) ? raw.errors : []
  const combined = [...errors, ...reportErrors]
    .map((item) => String(item || '').trim())
    .filter((item) => item.length > 0)
  return combined.length > 0 ? combined : undefined
}

function normalizeSyncMessage(raw, fallbackMessage) {
  const message = String(raw?.message || '').trim()
  if (message) return message
  const reportMessage = String(raw?.report?.message || '').trim()
  if (reportMessage) return reportMessage
  return fallbackMessage
}

export function normalizeSyncWithCodeResult(raw, fallbackMessage = 'Sync with code completed') {
  const ok = raw?.success !== false
  return {
    ok,
    message: normalizeSyncMessage(raw, fallbackMessage),
    stats: normalizeSyncStats(raw),
    errors: normalizeSyncErrors(raw),
  }
}

function normalizeSyncWithCodeError(error, fallbackMessage = 'Sync with code failed') {
  const responseData = error?.response?.data
  const message = normalizeSyncMessage(responseData, fallbackMessage)
  const err = new Error(message)
  err.ok = false
  err.stats = normalizeSyncStats(responseData)
  err.errors = normalizeSyncErrors(responseData)
  err.response = error?.response
  return err
}

function normalizeBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

function normalizeAutoTranslateCommand(command) {
  const sourceLanguage = String(command?.sourceLanguage || '').trim()
  const targetLanguages = Array.isArray(command?.targetLanguages)
    ? command.targetLanguages.map((item) => String(item || '').trim()).filter(Boolean)
    : []
  const scopes = Array.isArray(command?.scopes)
    ? command.scopes.map((item) => String(item || '').trim()).filter(Boolean)
    : []
  const mode = String(command?.mode || '').trim() || 'missing_only'

  return {
    sourceLanguage,
    targetLanguages,
    scopes,
    mode,
    overwrite: normalizeBoolean(command?.overwrite, false),
    dryRun: normalizeBoolean(command?.dryRun, false),
  }
}

function normalizeAutoTranslationJob(raw) {
  if (!raw || typeof raw !== 'object') return raw
  if (!raw.result || typeof raw.result !== 'object') return raw
  return {
    ...raw,
    result: {
      ...raw.result,
      dryRun: normalizeBoolean(raw.result.dryRun, false),
    },
  }
}

export const getStoredLocale = () => localStorage.getItem(STORAGE_LOCALE_KEY) || 'en'

export const setLocale = (locale) => {
  localStorage.setItem(STORAGE_LOCALE_KEY, locale)
}

export const getStoredTranslations = () => {
  try {
    const raw = localStorage.getItem(STORAGE_TRANSLATIONS_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

export const setStoredTranslations = (payload) => {
  localStorage.setItem(STORAGE_TRANSLATIONS_KEY, JSON.stringify(payload))
}

export const fetchTranslations = async (locale = 'en') => {
  const response = await apiClient.get('/translations', { params: { locale } })
  const translations = response.data.translations.reduce((acc, item) => {
    acc[item.key] = item.value
    return acc
  }, {})
  setStoredTranslations({ locale, items: translations })
  return translations
}

export const fetchI18nSyncStatus = async () => {
  const response = await apiClient.get('/admin/i18n/sync-status')
  return response.data
}

export const syncI18nWithCode = async () => {
  try {
    const response = await apiClient.post('/admin/i18n/sync-with-code')
    return normalizeSyncWithCodeResult(response.data)
  } catch (error) {
    throw normalizeSyncWithCodeError(error)
  }
}

export const runAutoTranslation = async (command) => {
  const payload = normalizeAutoTranslateCommand(command)
  const response = await apiClient.post('/admin/i18n/auto-translate', {
    ...payload,
    async: true,
  })
  return response.data
}

export const fetchAutoTranslationJob = async (jobId) => {
  const response = await apiClient.get(`/admin/i18n/auto-translate/jobs/${jobId}`)
  return normalizeAutoTranslationJob(response.data)
}

export const setActiveAutoTranslationJob = (payload) => {
  if (!payload || typeof payload !== 'object') return
  const jobId = String(payload.jobId || '').trim()
  if (!jobId) return
  localStorage.setItem(STORAGE_AUTO_TRANSLATION_JOB_KEY, JSON.stringify({
    jobId,
    startedAt: payload.startedAt || new Date().toISOString(),
  }))
}

export const getActiveAutoTranslationJob = () => {
  try {
    const raw = localStorage.getItem(STORAGE_AUTO_TRANSLATION_JOB_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const jobId = String(parsed?.jobId || '').trim()
    if (!jobId) return null
    return {
      jobId,
      startedAt: typeof parsed?.startedAt === 'string' ? parsed.startedAt : null,
    }
  } catch (_err) {
    return null
  }
}

export const clearActiveAutoTranslationJob = () => {
  localStorage.removeItem(STORAGE_AUTO_TRANSLATION_JOB_KEY)
}

export const translate = (key, fallback = '') => {
  const stored = getStoredTranslations()
  if (stored && stored.items && stored.items[key]) {
    return stored.items[key]
  }
  return fallback || key
}

export const getFallbackTranslations = async () => {
  return getStoredTranslations()
}
